import { Capacitor } from '@capacitor/core';

// Google and Apple both block their OAuth flow inside embedded webviews (Google:
// disallowed_useragent, enforced since 2023; Apple's own consent screen behaves the
// same way) — @react-oauth/google's <GoogleLogin> only works on the real web, and
// there's no web equivalent here at all. Inside the Capacitor app, both go through
// single-purpose native plugins instead — @capawesome/capacitor-apple-sign-in
// (wraps Apple's own AuthenticationServices, no third-party SDK) and
// @capawesome/capacitor-google-sign-in (wraps GoogleSignIn-iOS only) — rather than
// a bundled multi-provider plugin, specifically to avoid linking Facebook's SDK +
// Alamofire into the binary for a provider Agence never offers (see
// project-memory/context/decisions/auth-and-security.md for the full rationale).
//
// Google: initialize() takes the WEB OAuth client (REACT_APP_GOOGLE_CLIENT_ID) per
// the plugin's own docs — Google's native SDK then issues an ID token audienced for
// that client, so server/routes/auth.js's verifyIdToken doesn't need a second
// audience. The iOS-specific client (REACT_APP_GOOGLE_IOS_CLIENT_ID) is consumed
// natively via Info.plist's GIDClientID key, not passed from JS at all — this
// plugin reads it directly from the native config. Until REACT_APP_GOOGLE_IOS_CLIENT_ID
// is set, native Google sign-in is unavailable and the caller should hide the
// option entirely rather than fall back to the broken web widget.
//
// Apple: needs no external client ID at all — it verifies against the app's own
// Bundle ID once the "Sign in with Apple" capability is enabled in Xcode, so it's
// gated behind REACT_APP_APPLE_SIGNIN_ENABLED (a plain flag, not an ID) so it stays
// hidden until that capability is actually turned on.
const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
const WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_SIGNIN_ENABLED = process.env.REACT_APP_APPLE_SIGNIN_ENABLED === 'true';

export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

// 'ios' | 'android' | 'web'. Re-exported from here so callers that already depend on this
// module for platform questions don't each reach into @capacitor/core separately.
export function getPlatform() {
  return Capacitor.getPlatform();
}

export function isNativeGoogleAuthAvailable() {
  if (!isNativePlatform()) return false;
  // initialize({ clientId }) is REQUIRED by the plugin and, per its own docs, "must be a web
  // client ID on all platforms, even on Android and iOS" — on Android it becomes Credential
  // Manager's server client ID. Without it the plugin initializes with `undefined` and
  // sign-in fails on tap, so the button must not render at all.
  if (!WEB_CLIENT_ID) return false;
  // iOS additionally needs its own client, which the plugin reads natively from Info.plist's
  // GIDClientID — JS never passes it. Android has no such fallback and needs nothing further.
  if (Capacitor.getPlatform() === 'ios') return !!IOS_CLIENT_ID;
  return true;
}

export function isNativeAppleAuthAvailable() {
  // iOS only, deliberately. The plugin DOES ship an Android implementation, but it is Apple's
  // web flow (a WebView on appleid.apple.com) which needs an Apple Services ID + redirect URL
  // that Agence has not registered — on-device it fails with "redirectUrl must be provided."
  // Offering Apple sign-in on Android also has no purpose here: Google sign-in covers that
  // platform, and Apple's parity requirement (Guideline 4.8) is an App Store rule only.
  return isNativePlatform() && Capacitor.getPlatform() === 'ios' && APPLE_SIGNIN_ENABLED;
}

let googleInitPromise = null;

async function ensureGoogleInitialized() {
  if (!googleInitPromise) {
    googleInitPromise = (async () => {
      const { GoogleSignIn } = await import('@capawesome/capacitor-google-sign-in');
      await GoogleSignIn.initialize({ clientId: WEB_CLIENT_ID });
      // Wrapped in a plain object on purpose — NEVER return the plugin itself from an async
      // function or resolve a promise with it. A Capacitor plugin proxy answers any property
      // access with a function that forwards to native, INCLUDING `then`, so the promise
      // machinery sees a thenable and calls `GoogleSignIn.then(resolve, reject)`. That
      // forwards to native and rejects, which is what broke sign-in on device:
      //   Uncaught (in promise) Error: "GoogleSignIn.then()" is not implemented on android
      // Pinned by nativeSocialAuth.test.js.
      return { plugin: GoogleSignIn };
    })();
  }
  // Returns the WRAPPER, not the plugin. `return plugin` from an async function assimilates
  // it exactly the same way resolving a promise with it does — the unwrapping happens on the
  // return value either way. Callers destructure.
  return googleInitPromise;
}

/**
 * Runs the native Google sign-in flow and returns the same { credential } shape
 * @react-oauth/google's onSuccess callback provides, so existing handlers
 * (handleGoogleSuccess in Login.js/Register.js) work unchanged either way.
 * @returns {Promise<{ credential: string }>}
 */
export async function nativeGoogleSignIn() {
  const { plugin: GoogleSignIn } = await ensureGoogleInitialized();
  const res = await GoogleSignIn.signIn();
  if (!res?.idToken) throw new Error('Google sign-in did not return a token');
  return { credential: res.idToken };
}

/**
 * Runs the native Apple sign-in flow and returns the shape POST /auth/apple expects.
 * fullName is only present on the user's very first authorization ever — the caller
 * must send it along immediately, since Apple won't resend it on later sign-ins.
 * @returns {Promise<{ identityToken: string, fullName: { givenName: string|null, familyName: string|null } | null }>}
 */
export async function nativeAppleSignIn() {
  // SignInScope comes from the plugin itself on purpose — never hardcode these strings.
  // Its iOS layer (ios/Plugin/Classes/Options/SignInOptions.swift) switches on the exact
  // literals "EMAIL"/"FULL_NAME" and silently drops anything else via `default: break`.
  // This shipped as `['email', 'name']`, which meant we asked Apple for NO scopes at all, so
  // every identity token came back with no email and no name and /auth/apple had nothing to
  // match on — the Guideline 2.1(a) rejection of 2026-09-18 ("unable to Sign in with Apple").
  // A plain string array gives no build, type or runtime error when it's wrong, so taking the
  // values from the plugin's own enum is what makes the mistake structurally impossible.
  // Pinned by nativeSocialAuth.test.js.
  const { AppleSignIn, SignInScope } = await import('@capawesome/capacitor-apple-sign-in');
  const res = await AppleSignIn.signIn({ scopes: [SignInScope.Email, SignInScope.FullName] });
  if (!res?.idToken) throw new Error('Apple sign-in did not return a token');
  const { givenName, familyName } = res;
  return {
    identityToken: res.idToken,
    fullName: givenName || familyName ? { givenName: givenName || null, familyName: familyName || null } : null,
  };
}
